import { useMemo } from 'react';
import { Ruler, StickyNote, ImageIcon } from 'lucide-react';
import { useMedidas, type MedidaDTO } from '../../hooks/useMedidas';
import { useMedidaFotos, type MedidaFotoDTO } from '../../hooks/useMedidaFotos';
import { useProject } from '../../hooks/useProjects';
import { FotoThumbnail } from './FotoThumbnail';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';

const RUBRO_LABELS: Record<string, string> = {
  gypsum: 'Gypsum',
  pintura: 'Pintura',
  impermeabilizacion: 'Impermeabilización',
  empaste: 'Empaste',
};

function getBadgeColor(rubro: string): 'blue' | 'green' | 'orange' | 'gray' {
  if (rubro === 'gypsum') return 'blue';
  if (rubro === 'pintura') return 'green';
  if (rubro === 'impermeabilizacion' || rubro === 'empaste') return 'orange';
  return 'gray';
}

export function MedidasReferencePanel({ projectId }: { projectId: string }) {
  const { data: medidas } = useMedidas(projectId);
  const { data: fotos } = useMedidaFotos(projectId);
  const { data: project } = useProject(projectId);

  // Group medidas by rubro_tipo
  const groupedMedidas = useMemo(() => {
    const map = new Map<string, MedidaDTO[]>();
    if (!medidas) return map;
    for (const m of medidas) {
      const list = map.get(m.rubro_tipo) ?? [];
      list.push(m);
      map.set(m.rubro_tipo, list);
    }
    return map;
  }, [medidas]);

  // Group fotos by medida_id — fotos without medida_id go to '__orphan__'
  const groupedFotos = useMemo(() => {
    const map = new Map<string, MedidaFotoDTO[]>();
    if (!fotos) return map;
    for (const f of fotos) {
      const key = f.medida_id ?? '__orphan__';
      const list = map.get(key) ?? [];
      list.push(f);
      map.set(key, list);
    }
    return map;
  }, [fotos]);

  const orphanFotos = groupedFotos.get('__orphan__') ?? [];

  return (
    <Card>
      <div className="p-4 space-y-4">
        {/* Title */}
        <div className="flex items-center gap-2">
          <Ruler size={16} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-700">Medidas del Residente</h3>
        </div>

        {/* No medidas */}
        {(!medidas || medidas.length === 0) && (
          <p className="text-sm text-gray-400 italic text-center py-2">Sin medidas registradas</p>
        )}

        {/* Observaciones generales del proyecto */}
        {project?.medidas_notas && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
            <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
              <StickyNote size={12} />
              Observaciones generales
            </p>
            <p className="text-xs text-amber-700 whitespace-pre-wrap">{project.medidas_notas}</p>
          </div>
        )}

        {/* Medidas grouped by rubro */}
        {medidas && medidas.length > 0 && (
          <div className="space-y-4">
            {Array.from(groupedMedidas.entries()).map(([rubro, items]) => (
              <div key={rubro}>
                <Badge color={getBadgeColor(rubro)}>
                  {RUBRO_LABELS[rubro] ?? rubro}
                </Badge>
                <div className="mt-2 space-y-2">
                  {items.map((m) => {
                    const medidaFotos = groupedFotos.get(m.id) ?? [];
                    return (
                      <div key={m.id} className="text-xs text-gray-600 border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                        {/* Item line */}
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{m.item_tipo}</span>
                          <span className="font-semibold shrink-0 ml-4 text-gray-800">
                            {m.cantidad} {m.unidad}
                          </span>
                        </div>

                        {/* Notas de la medida */}
                        {m.notas && (
                          <div className="mt-1 flex items-start gap-1">
                            <StickyNote size={11} className="text-amber-500 mt-0.5 shrink-0" />
                            <span className="bg-amber-50 text-amber-700 rounded px-2 py-1 text-xs leading-snug">
                              {m.notas}
                            </span>
                          </div>
                        )}

                        {/* Fotos vinculadas a esta medida */}
                        {medidaFotos.length > 0 && (
                          <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                            {medidaFotos.map((foto) => (
                              <FotoThumbnail
                                key={foto.id}
                                s3Key={foto.s3_key}
                                filename={foto.filename}
                                mimeType={foto.mime_type}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Fotos generales del proyecto (orphans) */}
        {orphanFotos.length > 0 && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5 mb-2">
              <ImageIcon size={13} className="text-gray-400" />
              <span className="text-xs font-semibold text-gray-500">
                Fotos generales del proyecto ({orphanFotos.length})
              </span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {orphanFotos.map((foto) => (
                <FotoThumbnail
                  key={foto.id}
                  s3Key={foto.s3_key}
                  filename={foto.filename}
                  mimeType={foto.mime_type}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
